import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Loader from "../../Loader";
import { PaymentDateFilters } from "../AdminPayments/PaymentFilters";
import { GET_CASH_IN_HAND_REPORT } from "./graphql";

const todayStr = () => new Date().toISOString().slice(0, 10);

const CashInHandReport = () => {
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());

  const [fetchReport, { data, loading, error }] = useLazyQuery(
    GET_CASH_IN_HAND_REPORT,
    { fetchPolicy: "network-only" },
  );

  const applyDateFilter = () => {
    fetchReport({
      variables: {
        data: {
          startDate: startDate || null,
          endDate: endDate || null,
        },
      },
    });
  };

  useEffect(() => {
    applyDateFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const report = data?.getCashInHandReport;
  const rows = report?.rows || [];
  const totalCash = report?.totalCash || 0;
  const totalOnline = report?.totalOnline || 0;

  return (
    <div className="min-h-screen flex">
      <Sidebar />

      <Layout>
        <div className="p-6 bg-white w-full rounded-lg shadow-md m-4 overflow-y-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 border-b pb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Cash In Hand Report
              </h1>
              <p className="text-sm text-gray-500">
                How much cash each employee is holding vs. settled online, for
                the selected date range.
              </p>
            </div>

            <PaymentDateFilters
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              applyDateFilter={applyDateFilter}
            />
          </div>

          {loading && <Loader />}

          {!loading && error && (
            <div className="rounded-lg border border-dashed border-red-300 bg-red-50 p-6 text-center text-red-600">
              Error fetching cash in hand report.
            </div>
          )}

          {!loading && !error && report?.status && report.status !== "SUCCESS" && (
            <div className="rounded-lg border border-dashed border-red-300 bg-red-50 p-6 text-center text-red-600">
              {report.message || "Failed to fetch cash in hand report."}
            </div>
          )}

          {!loading && !error && (
            <>
              {/* SUMMARY CARDS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div className="bg-white shadow rounded-xl p-5 border border-green-100">
                  <p className="text-gray-500">Total Cash In Hand</p>
                  <h2 className="text-3xl font-bold text-green-600">
                    ₹ {totalCash}
                  </h2>
                </div>

                <div className="bg-white shadow rounded-xl p-5 border border-blue-100">
                  <p className="text-gray-500">Total Settled Online</p>
                  <h2 className="text-3xl font-bold text-blue-600">
                    ₹ {totalOnline}
                  </h2>
                </div>
              </div>

              {/* TABLE */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-left text-sm text-gray-600">
                  <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                    <tr>
                      <th className="py-3 px-4">Employee</th>
                      <th className="py-3 px-4">Cash Collected</th>
                      <th className="py-3 px-4">Online Settled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-6 text-gray-400">
                          No settlements found for the selected date range.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.employeeId} className="hover:bg-gray-50 transition">
                          <td className="py-3 px-4 font-semibold text-gray-800">
                            {row.employeeName || row.employeeId}
                          </td>
                          <td className="py-3 px-4 text-green-600 font-medium">
                            ₹ {row.cashAmount || 0}
                          </td>
                          <td className="py-3 px-4 text-blue-600 font-medium">
                            ₹ {row.onlineAmount || 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-50 font-bold border-t">
                        <td className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-green-700">₹ {totalCash}</td>
                        <td className="py-3 px-4 text-blue-700">₹ {totalOnline}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default CashInHandReport;
